import {extendObservable, makeAutoObservable, runInAction} from 'mobx';

import type {FoodTrackerApiModel} from '../../../api/foodDiary/foodTracker/types';
import type {WithBusyManager} from '../../../utils/store';
import type {
  Schema,
  SetTrackerParams,
  SharingSettingSchema,
  Tracker,
  TrackerSlug,
} from './types';

import {foodTrackerStore} from '../../../api/foodDiary/foodTracker/';
import {alert} from '../../../components/alert';
import {getRandomColor} from '../../../styles/utils/getRandomColor';
import {not} from '../../../utils/conditionalUtils';
import {
  getFoodDiaryTrackerTitle,
  getFoodDiaryTrackerUnitString,
} from '../../../utils/entity/foodDiary';
import {logger} from '../../../utils/logger';
import {AsyncBusyManager} from '../../../utils/store';

type BusyReason = 'create' | 'update';

export class FoodDiaryTrackersStore implements WithBusyManager<BusyReason> {
  readonly busy = new AsyncBusyManager<BusyReason>();
  isLoaded = false;
  mainTrackerSlug: null | TrackerSlug = null;
  selectedTrackers: {[key: string]: Tracker} = {};
  sharingSettings: FoodTrackerApiModel['sharing_settings'] = {
    food_list: false,
    notes: false,
    nutrients: false,
    photo: false,
  };
  showTrackersData = false;
  private availableTrackers: Schema;
  private foodTrackerRecord: FoodTrackerApiModel;
  private trackers: Tracker[] = [];

  constructor() {
    this.busy.observe(this, {
      createTrackers: 'create',
      updateTrackers: 'update',
    });

    this.init();

    makeAutoObservable(
      this,
      {
        getTrackerLabel: false,
        getTrackerUnit: false,
      },
      {autoBind: true},
    );
  }

  private async init() {
    try {
      const foodTrackerRecord = await foodTrackerStore.read();
      const schema = await foodTrackerStore.getSchema();

      runInAction(() => {
        this.availableTrackers = schema;

        if (foodTrackerRecord !== null) {
          this.processTrackersRecord(foodTrackerRecord);
        }

        this.trackers = this.createDefaultTrackersList(schema);
        this.isLoaded = true;
      });
    } catch (error) {
      logger.error(error);
      alert.error(error);
    }
  }

  get hasPermittedSharingSetting() {
    return Object.values(this.sharingSettings).some(isPermitted => isPermitted);
  }

  get isFetching() {
    return Boolean(this.isUpdating) || not(this.isLoaded);
  }

  get isUpdating() {
    return this.busy.getReason('create') || this.busy.getReason('update');
  }

  get mainTracker() {
    if (!this.mainTrackerSlug) {
      return;
    }

    return {
      ...this.selectedTrackers[this.mainTrackerSlug]!,
      ...this.availableTrackers[this.mainTrackerSlug]!,
    };
  }

  get needShowMainTracker(): boolean {
    return this.showTrackersData;
  }

  get renderSelectedTrackersList(): Tracker[] {
    if (this.mainTrackerSlug) {
      return this.selectedTrackersList.filter(
        tracker => tracker.slug !== this.mainTrackerSlug,
      );
    }

    return this.selectedTrackersList;
  }

  get selectedTrackersList(): Tracker[] {
    const trackersList = Object.keys(this.selectedTrackers).map(slug => {
      return {
        amount: this.selectedTrackers[slug]!.amount,
        slug: slug as TrackerSlug,
      };
    });

    return trackersList.sort((a, b) => a.slug.localeCompare(b.slug));
  }

  get trackersList() {
    return this.trackers.slice().sort((a, b) => a.slug.localeCompare(b.slug));
  }

  async createTrackers() {
    if (this.foodTrackerRecord) {
      return;
    }

    const params = {
      main: this.mainTrackerSlug,
      nutrients: this.createParamsNutrients(),
      show_tracker_data: this.showTrackersData,
    };

    try {
      const foodTrackerRecord = await foodTrackerStore.create(params);

      this.processTrackersRecord(foodTrackerRecord);
    } catch (error) {
      logger.error(error);

      throw error;
    }
  }

  getTrackerColor(slug: TrackerSlug): string {
    return getRandomColor(slug);
  }

  getTrackerLabel(slug: TrackerSlug): string {
    const title = getFoodDiaryTrackerTitle(slug);
    const unit = getFoodDiaryTrackerUnitString(this.getTrackerUnit(slug));

    return `${title} (${unit})`;
  }

  getTrackerUnit(slug: TrackerSlug): string {
    return this.availableTrackers[slug]!.unit;
  }

  setMainTrackerSlug(slug: TrackerSlug) {
    if (slug === this.mainTrackerSlug) {
      this.mainTrackerSlug = null;
    } else {
      this.mainTrackerSlug = slug;
    }

    this.processMainTracker();
  }

  setSharingSetting(sharingSetting: SharingSettingSchema) {
    this.sharingSettings = sharingSetting;
  }

  setShowMainTracker(isShow: boolean) {
    this.showTrackersData = isShow;
  }

  setTracker({amount, isTrackerSelected, slug}: SetTrackerParams): void {
    if (isTrackerSelected) {
      this.selectedTrackers[slug] = {
        ...this.selectedTrackers[slug],
        amount: amount || 0,
        slug: slug,
      };
    } else {
      delete this.selectedTrackers[slug];

      if (slug === this.mainTrackerSlug) {
        this.mainTrackerSlug = null;
      }
    }

    this.processMainTracker();
  }

  setTrackerGoal(slug: string, amount: string) {
    this.selectedTrackers[slug] = {
      ...this.selectedTrackers[slug]!,
      amount: Number(amount) || 0,
    };
  }

  async updateTrackers() {
    const params = {
      main: this.mainTrackerSlug,
      nutrients: this.createParamsNutrients(),
      sharing_settings: this.sharingSettings,
      show_tracker_data: this.showTrackersData,
    };

    try {
      const foodTrackerRecord = await foodTrackerStore.update(
        this.foodTrackerRecord,
        params,
      );

      this.processTrackersRecord(foodTrackerRecord);
    } catch (error) {
      logger.error(error);

      throw error;
    }
  }

  private createDefaultTrackersList(schema: Schema): Tracker[] {
    const keys = Object.keys(schema);

    return keys.map(slug => {
      if (Object.prototype.hasOwnProperty.call(this.selectedTrackers, slug)) {
        return {
          amount: this.selectedTrackers[slug]!.amount || 0,
          slug: slug as TrackerSlug,
        };
      }

      return {
        amount: 0,
        slug: slug as TrackerSlug,
      };
    });
  }

  private createParamsNutrients() {
    const paramsNutrients: Tracker[] = [];

    for (const nutrient in this.selectedTrackers) {
      paramsNutrients.push(this.selectedTrackers[nutrient]!);
    }

    return paramsNutrients;
  }

  private processMainTracker() {
    const selectedTrackersCount = Object.keys(this.selectedTrackers).length;

    if (selectedTrackersCount > 0 && !this.mainTrackerSlug) {
      const {slug: firstSelectedTrackerSlug} = this.selectedTrackersList[0]!;

      this.setMainTrackerSlug(firstSelectedTrackerSlug);
    } else if (selectedTrackersCount === 0 && this.mainTrackerSlug) {
      this.mainTrackerSlug = null;
    }
  }

  private processTrackersRecord(record: FoodTrackerApiModel) {
    this.selectedTrackers = {};

    record.nutrients.forEach(nutrient => {
      extendObservable(this.selectedTrackers, {
        [nutrient.slug]: {
          ...nutrient,
        },
      });
    });

    this.foodTrackerRecord = record;
    this.mainTrackerSlug = record.main;
    this.showTrackersData = record.show_tracker_data;
    this.sharingSettings = record.sharing_settings;
  }
}
